import type { ProductAttributes, PublicAttribute } from 'src/redux/product/types';
import type {
  ContentstackFacetConfig,
  ContentstackListingsControlsConfig,
} from 'src/services/contentstack/types/ContentstackListingsControlsConfig';

/**
 * Converts a facet config key (e.g., 'attributes.highly_rated') to a camelCase property name (e.g., 'highlyRated')
 */
const facetKeyToAttributeKey = (facetKey: string): string => {
  const attributeKey = facetKey.replace('attributes.', '').replace('pim_', '');
  return attributeKey.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Gets the attribute value from the unified product attributes, normalizing it to an array
 */
const getAttributeValue = (
  attributes: ProductAttributes,
  key: string,
): PublicAttribute['values'] | null => {
  const value = attributes[key];

  if (value === null || value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    return value;
  }

  return [value];
};

export const adaptProductPublicAttributesToStore = (
  productAttributes: ProductAttributes | null,
  listingsControlsConfig: ContentstackListingsControlsConfig | null,
): PublicAttribute[] => {
  if (!productAttributes || !listingsControlsConfig) {
    return [];
  }

  const allFacetConfigs: ContentstackFacetConfig[] = [
    ...(listingsControlsConfig.facet_static_config || []),
    ...(listingsControlsConfig.facet_dynamic_config || []),
  ];

  const result: PublicAttribute[] = [];

  for (const facetConfig of allFacetConfigs) {
    const attributeKey = facetKeyToAttributeKey(facetConfig.key);
    const attributeValue = getAttributeValue(productAttributes, attributeKey);

    if (attributeValue !== null) {
      result.push({
        key: facetConfig.key,
        title: facetConfig.title,
        values: attributeValue,
      });
    }
  }

  return result;
};
